import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from "react";

/**
 * Allows any page/scene to configure what the mobile Header shows:
 * - title: centred text (e.g. "Valuations", property address)
 * - back: show a back arrow instead of the logo
 * - redirect: where the back arrow navigates (falls back to router.back())
 * - search: { value, onChange, placeholder } — shows magnifying glass icon
 *           that expands an animated search bar under the nav
 *
 * On desktop the Header is unchanged — this context only affects < lg.
 */
const MobileHeaderContext = createContext({
  mobileTitle: null,
  mobileBack: false,
  mobileRedirect: null,
  mobileSearch: null,
  mobileActions: null,
  setMobileHeader: () => {},
  setMobileHeaderActions: () => {},
});

export const MobileHeaderProvider = ({ children }) => {
  const [state, setState] = useState({
    mobileTitle: null,
    mobileBack: false,
    mobileRedirect: null,
    mobileSearch: null,
  });
  // `actions` is kept in its own state slice so scenes can set/clear it
  // independently of the route-level title/back/redirect configuration.
  // Moved out of the main state object to avoid page-level useSetMobileHeader
  // calls wiping scene-level actions on re-render.
  const [mobileActions, setMobileActions] = useState(null);

  // Use a ref for search to avoid stale closure issues with onChange
  const searchRef = useRef(null);

  const setMobileHeader = useCallback(({ title, back, redirect, search } = {}) => {
    searchRef.current = search ?? null;
    const next = {
      mobileTitle: title ?? null,
      mobileBack: !!back,
      mobileRedirect: redirect ?? null,
      mobileSearch: search ?? null,
    };
    // Bail out when nothing actually changed: every call used to hand React a
    // fresh object, so re-declaring an identical header re-rendered the app.
    setState(prev =>
      prev.mobileTitle === next.mobileTitle &&
      prev.mobileBack === next.mobileBack &&
      prev.mobileRedirect === next.mobileRedirect &&
      prev.mobileSearch === next.mobileSearch
        ? prev
        : next
    );
  }, []);

  const setMobileHeaderActions = useCallback((actions) => {
    setMobileActions(actions ?? null);
  }, []);

  // Memoised: this object is the context value, so rebuilding it on every
  // render re-rendered every consumer — which includes AppShell, the Header and
  // whichever scene is on screen. Those re-renders cost real frames on a page
  // as large as the valuation detail view.
  const value = useMemo(
    () => ({
      mobileTitle: state.mobileTitle,
      mobileBack: state.mobileBack,
      mobileRedirect: state.mobileRedirect,
      mobileSearch: state.mobileSearch,
      mobileActions,
      searchRef,
      setMobileHeader,
      setMobileHeaderActions,
    }),
    [state, mobileActions, setMobileHeader, setMobileHeaderActions]
  );

  return (
    <MobileHeaderContext.Provider value={value}>
      {children}
    </MobileHeaderContext.Provider>
  );
};

export const useMobileHeader = () => useContext(MobileHeaderContext);

/**
 * Hook for pages/scenes to declare their mobile header on mount.
 * Cleans up on unmount so the header reverts to the default (logo + hamburger).
 *
 * `search` is kept in a ref to avoid re-render loops — the Header reads
 * live values from searchRef.current, so the object identity doesn't matter.
 */
export const useSetMobileHeader = ({ title, back, redirect, search } = {}) => {
  const { setMobileHeader, searchRef } = useMobileHeader();
  const hasSearch = !!search;

  // Keep searchRef in sync without triggering re-renders
  useEffect(() => {
    if (searchRef) searchRef.current = search ?? null;
  });

  useEffect(() => {
    setMobileHeader({ title, back, redirect, search: hasSearch ? search : null });
    return () => setMobileHeader({});
    // Only re-run when title/back/redirect change, or search presence toggles
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, back, redirect, hasSearch, setMobileHeader]);
};

/**
 * Hook for scenes to declare a right-aligned action slot for the mobile
 * header (e.g. a status select, per-valuation action buttons). Cleared on
 * unmount so no stale actions persist across pages.
 *
 * Kept separate from `useSetMobileHeader` so a page-level title config and
 * a scene-level actions config don't wipe each other on render — each hook
 * owns its own slice of the context.
 */
export const useSetMobileHeaderActions = (actions) => {
  const { setMobileHeaderActions } = useMobileHeader();

  useEffect(() => {
    setMobileHeaderActions(actions);
    return () => setMobileHeaderActions(null);
  }, [actions, setMobileHeaderActions]);
};
